import type { ReactNode } from 'react';
import * as React from 'react';
import { useEffect, useRef, useState } from 'react';

import omit from 'lodash/omit';
import PropTypes from 'prop-types';

import Retracked from 'js/app/retracked';

import { StyleSheet, css } from '@coursera/coursera-ui';

import Submit from 'bundles/authentication/components/Submit';
import { LOGIN_REDESIGN_RECAPTCHA_ID, RECAPTCHA_ACTIONS } from 'bundles/authentication/constants';
import Alert from 'bundles/authentication/shared/Alert';
import EmailInput from 'bundles/authentication/shared/EmailInput';
import Form from 'bundles/authentication/shared/Form';
import PasswordInput from 'bundles/authentication/shared/PasswordInput';
import TwoFactorAuthInput from 'bundles/authentication/shared/TwoFactorAuthInput';
import * as actions from 'bundles/authentication/shared/actions';
import LinkButton from 'bundles/authentication/shared/components/authentication-modal/LinkButton';
import type { ReCAPTCHA } from 'bundles/authentication/shared/reCAPTCHA';
import reCAPTCHA from 'bundles/authentication/shared/reCAPTCHA';
import type { LoginValues, LoginSharedProps as Props } from 'bundles/authentication/shared/types/sharedTypes';
import { HANDLED_ERRORS, NEEDS_2FA_ERRORS, errorMessageFactory } from 'bundles/authentication/utils/loginUtils';
import withSingleTracked from 'bundles/common/components/withSingleTracked';
import GrowthAcquisitionExperiments from 'bundles/epic/clients/GrowthAcquisition';
import authenticationExperiments from 'bundles/epic/clients/authentication';
import { trackAuthAttempts } from 'bundles/user-account/utils/authTrackingUtils';

import _t from 'i18n!nls/authentication';

const TrackedLinkButton = withSingleTracked({ type: 'BUTTON' })(LinkButton);
const TrackedSubmit = withSingleTracked({ type: 'BUTTON' })(Submit);

const styles = StyleSheet.create({
  form: {
    // This is an offset of the marginTop below to keep the space above consistent
    marginTop: '-12px',
  },
  inputContainer: {
    marginTop: '12px',
    display: 'grid',
    gridGap: '12px',
    gridTemplateColumns: '1fr',
  },
  experiment: {
    display: 'flex',
    'justify-content': 'space-between',
  },
  item: {
    ':nth-child(1)': {
      '-ms-grid-row': '1',
    },
    ':nth-child(2)': {
      '-ms-grid-row': '2',
    },
    ':nth-child(3)': {
      '-ms-grid-row': '3',
    },
    ':nth-child(4)': {
      '-ms-grid-row': '4',
    },
    ':nth-child(5)': {
      '-ms-grid-row': '5',
    },
  },
  reCAPTCHA: {
    display: 'flex',
    'justify-content': 'center',
    '&.checkbox': { 'margin-top': '8px' },
    ':nth-of-type(n) .grecaptcha-badge': { display: 'none !important' },
  },
  submit: {
    marginTop: '12px',
  },
  styleConflictInputFix: {
    ':nth-of-type(n) input': {
      border: `0px !important`,
      height: 'unset',
      marginBottom: '0px !important',
      padding: '12px 16px',
      lineHeight: '22px',
      fontSize: '16px',
      outline: 'none',
      boxShadow: 'none !important',
      color: 'unset',
    },
    ':nth-of-type(n) input:focus:required:invalid:focus': { color: 'unset' },
    ':nth-of-type(n) input::placeholder': { color: '#767676', opacity: 1 },
    /* Internet Explorer 10-11 */
    ':nth-of-type(n) input:-ms-input-placeholder': { color: '#767676', opacity: 1 },
    /* Edge */
    ':nth-of-type(n) input:-webkit-input-placeholder': { color: '#767676', opacity: 1 },
  },
});

type EventData = {
  namespace: {
    app: string;
    page: string;
  };
};

type Context = {
  _eventData: EventData;
};

const LoginForm = (props: Props, { _eventData }: Context) => {
  const [has2FA, setHas2FA] = useState(false);
  const [isRunning, setIsRunning] = useState(false);
  const [error, setError] = useState<ReactNode>();

  const captcha = useRef<ReCAPTCHA | null>(null);
  const input = useRef<HTMLInputElement>();
  const isCheckbox = GrowthAcquisitionExperiments.get('useEnterpriseCheckboxReCaptchaLogin');

  useEffect(() => {
    captcha.current = reCAPTCHA(LOGIN_REDESIGN_RECAPTCHA_ID, {
      _eventData,
      checkbox: isCheckbox,
      action: RECAPTCHA_ACTIONS.login,
    });

    return function cleanup() {
      captcha.current = null;
    };
  }, [_eventData, isCheckbox]);

  const handleError = (response: $TSFixMe) => {
    const { onError = () => {}, onForgotPassword } = props;
    const enableMagicLinks = GrowthAcquisitionExperiments.get('magicLinksEnabled');

    captcha.current?.reset();

    const { errorCode: code } = response?.responseJSON || {};

    if (HANDLED_ERRORS.includes(code)) {
      if (code === 'invalidCredential') {
        // @ts-expect-error TS7016 TODO: code has type any
        setError(enableMagicLinks ? errorMessageFactory()[code](onForgotPassword) : errorMessageFactory()[code]());
        setHas2FA(NEEDS_2FA_ERRORS.includes(code));
        setIsRunning(false);
        input.current?.focus();
      } else {
        // @ts-expect-error TS7016 TODO: code has type any
        setError(errorMessageFactory()[code]);
        setHas2FA(NEEDS_2FA_ERRORS.includes(code));
        setIsRunning(false);
      }
    } else {
      onError(response);
    }
  };

  const login = async (values: LoginValues) => {
    const { onSuccess } = props;
    if (!values.email || !values.password) {
      return setError(errorMessageFactory().invalidRequest);
    }

    const token = await captcha.current?.getResponse();

    if (!token) {
      return setError(isCheckbox ? errorMessageFactory().recaptchaFailed : '');
    }

    setIsRunning(true);

    const payload = {
      ...omit(values, 'one-time-password'),
      token,
      ...(values['one-time-password'] && { mfa: values['one-time-password'] }),
    };

    trackAuthAttempts(_eventData, 'login');

    try {
      const response = await actions.login(payload);

      return onSuccess(response?.redirectTo as string);
    } catch (error) {
      return handleError(error);
    }
  };

  const onInvalid = () => {
    setError(undefined);
  };

  const renderMagicLinks = () => {
    const { onForgotPassword, onRequestMagicLink } = props;
    const enableMagicLinks = authenticationExperiments.get('enableMagicLinks');
    switch (enableMagicLinks) {
      case 'A':
        return (
          <div {...css(styles.experiment)}>
            <TrackedLinkButton onClick={onForgotPassword} trackingName="login_form_forgot_password_link">
              {_t('Forgot password?')}
            </TrackedLinkButton>
            <TrackedLinkButton onClick={onRequestMagicLink} trackingName="login_form_login_link_link">
              {_t('Use login link')}
            </TrackedLinkButton>
          </div>
        );
      case 'B':
        return (
          <>
            <TrackedLinkButton onClick={onForgotPassword} trackingName="login_form_forgot_password_link">
              {_t('Reset password')}
            </TrackedLinkButton>{' '}
            {_t('or')}{' '}
            <TrackedLinkButton onClick={onRequestMagicLink} trackingName="login_form_login_link_link">
              {_t('email me a login link')}
            </TrackedLinkButton>
          </>
        );
      case 'control':
      default:
        return (
          <TrackedLinkButton onClick={onForgotPassword} trackingName="login_form_forgot_password_link">
            {_t('Forgot password?')}
          </TrackedLinkButton>
        );
    }
  };

  const { defaultEmail } = props;

  return (
    <Form {...css('rc-LoginForm', styles.form)} name="login" onSubmit={login} onInvalid={onInvalid}>
      <div aria-live="assertive">
        {error && !isRunning && (
          <Alert {...css(styles.item)} type="error">
            {error}
          </Alert>
        )}
      </div>
      <div {...css(styles.inputContainer)}>
        <EmailInput
          {...css(styles.styleConflictInputFix, styles.item)}
          autoFocus
          data-e2e="login-email-input"
          defaultValue={defaultEmail}
          ref={input}
          required
        />
        <PasswordInput
          {...css(styles.styleConflictInputFix, styles.item)}
          data-e2e="login-password-input"
          hint={renderMagicLinks()}
          placeholder={_t('Enter your password')}
          autoComplete="current-password"
          required
        />
        {has2FA && (
          <TwoFactorAuthInput
            {...css(styles.styleConflictInputFix, styles.item)}
            autoFocus
            data-e2e="verification-code-field"
          />
        )}
        <div
          {...css(styles.reCAPTCHA, isCheckbox && 'checkbox')}
          id={LOGIN_REDESIGN_RECAPTCHA_ID}
          data-size={!isCheckbox && 'invisible'}
        />
        <TrackedSubmit
          {...css(styles.submit, styles.item)}
          data-e2e="login-form-submit-button"
          disabled={isRunning}
          trackingName="login_form_submit_button"
        >
          {_t('Login')}
        </TrackedSubmit>
      </div>
    </Form>
  );
};

LoginForm.contextTypes = {
  _eventData: PropTypes.object.isRequired,
};

export default Retracked.createTrackedContainer<Props>(() => ({
  namespace: {
    page: 'login',
  },
}))(LoginForm);
